import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import Body1 from '../Typography/Body/Body1';
import Button from '../Button';
import UnstyledShareIcons, { SocialProperties } from '../SocialShare';
import Headline6 from '../Typography/Heading/Headline6';
import { Grid, GridCell } from '../Grid';
import breakpoints from '../Theme/breakpoints';
import Overline from '../Typography/Overline';
import { mediumRoundedCorners } from '../RoundedCorners';

const ShareIcons = styled(
  UnstyledShareIcons as React.ComponentType<SocialProperties>,
)`
  margin: 1rem 0 0 -.5rem;
`;

const StyledHeadline6 = styled(Headline6)`
  margin-bottom: 12px;
`;

export const BackButton = styled(Button)`
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

export const ActionButton = styled(Button)`
  float: right;
`;

const ImageContainer = styled.div`
  flex: 1 0 100%;
  justify-content: center;
  display: inline-flex;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
  ${mediumRoundedCorners};

  @media (min-width: ${breakpoints.md}px) {
    display: flex;
    justify-content: right;
    margin: 0 1.5rem 0 0;
    flex: 2 0 calc(62% - 1.5rem);
  }
`;

const DetailsContainer = styled.div`
  flex: 1 0 100%;
  margin: 1rem auto;
  max-width: none;

  && {
    @media (min-width: ${breakpoints.md}px) {
      flex: 1 0 38%;
      margin: 0 auto;
    }
  }
`;

const LookBookImageDetailWrapper = styled.div`
  max-width: 1416px;
  && {
    margin: 0 auto;
    @media (min-width: ${breakpoints.sm}px) {
      padding: 0 0 24px;
    }
  }
  display: flex;
  flex-wrap: wrap;
  justify-content: start;
`;

const BackButtonContainer = styled.div`
  margin: 1.5rem 0;
  flex: 1 0 calc(100% - 12px);
`;

const UpdateDetailsContainer = styled.div`
  margin: 1rem 0 0;
  flex: 1 0 calc(100% - 12px);
  width: 100%;

  pre {
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  display: flex;
  flex-direction: column;

  @media (min-width: ${breakpoints.md}px) {
    display: block;
  }
`;

const ButtonsContainer = styled.div`
  button {
    margin-right: .5rem;
  }

  margin-right: auto;

  @media (min-width: ${breakpoints.md}px) {
    margin-right: initial;
  }
`;

const CardOverlineContent = styled(Overline)`
  display: flex;
  align-items: center;
  margin-bottom: .5rem;
`;

const lookbookImagePropertyTypes = {
  image: PropTypes.node.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  createdAt: PropTypes.string,
  credits: PropTypes.string,
  creditsLabel: PropTypes.string,
  name: PropTypes.string,
  categoryTitle: PropTypes.string,
  originalUrl: PropTypes.string,
  shareData: PropTypes.arrayOf(
    PropTypes.shape({}),
  ),
  backButton: PropTypes.node.isRequired,
  removeButton: PropTypes.node,
  updateButton: PropTypes.node,
};

type LookbookImageProperties = Omit<
  InferProps<typeof lookbookImagePropertyTypes>,
  'shareData'
> & {
  shareData?: SocialProperties['data'];
};

const LookbookImage = ({
  image,
  title,
  description,
  createdAt,
  credits,
  creditsLabel,
  name,
  categoryTitle,
  originalUrl,
  shareData,
  backButton,
  removeButton,
  updateButton,
}: LookbookImageProperties) => (
  <Grid spacing={6}>
    <GridCell columns={12} padding="1rem 1rem">
      <LookBookImageDetailWrapper>
        <ImageContainer>
          {image}
        </ImageContainer>
        <DetailsContainer>
          <StyledHeadline6 tag="h1">
            {title}
          </StyledHeadline6>
          {credits && (
            <CardOverlineContent tag="div">
              {creditsLabel ?? 'Credits'}
              :
              {' '}
              {credits}
            </CardOverlineContent>
          )}
          {name && (
            <CardOverlineContent tag="div">
              {`Uploaded by ${name}`}
            </CardOverlineContent>
          )}
          <CardOverlineContent tag="div">
            {createdAt}
          </CardOverlineContent>
          <CardOverlineContent tag="div">
            {categoryTitle}
          </CardOverlineContent>
          <Body1>
            {description}
          </Body1>
          <ShareIcons
            data={shareData}
          />
        </DetailsContainer>
        <UpdateDetailsContainer>
          <ButtonsContainer>
            {removeButton}
            {updateButton}
          </ButtonsContainer>
          {originalUrl && (
            <pre>
              {originalUrl}
            </pre>
          )}
        </UpdateDetailsContainer>
        <BackButtonContainer>
          {backButton}
        </BackButtonContainer>
      </LookBookImageDetailWrapper>
    </GridCell>
  </Grid>
);

LookbookImage.propTypes = lookbookImagePropertyTypes;

export default LookbookImage;
